import * as zod from 'zod';

export const Robotiq2FControlKind = zod.enum(['move', 'calibrate']);

export const Robotiq2FCommand = zod.object({
  kind: zod.literal('Robotiq2FCommand'),
  controlKind: Robotiq2FControlKind,
  /**
   * Motion parameters. Required for a 'move' and absent for a 'calibrate' —
   * enforced by the device implementations rather than the schema, which must
   * stay a plain object to remain a valid `DeviceCommand` discriminated-union
   * member. A calibrate command must not carry placeholder values here:
   * arm-control-bot builds that predate the 'calibrate' verb ignore
   * `controlKind`, so absent fields make them reject the command instead of
   * executing it as a move.
   */
  targetGripWidth: zod.number().nonnegative().optional(),
  targetSpeed: zod.number().nonnegative().optional(),
  targetForce: zod.number().nonnegative().optional(),
  /**
   * When set, a closing actuation must end with the jaws stopped on a part at
   * `targetGripWidth` (within `targetDiameterTolerance`) or it is reported as a
   * failed grip. Omitted by jog/stream callers, which never grip.
   */
  waitForGripToContinue: zod.boolean().optional(),
  /** How far (meters) the achieved grip width may sit from the target. */
  targetDiameterTolerance: zod.number().nonnegative().optional(),
});

export type Robotiq2FCommand = zod.infer<typeof Robotiq2FCommand>;

/** A move command with its motion parameters proven present. */
export type Robotiq2FMoveCommand = Robotiq2FCommand & {
  controlKind: 'move';
  targetGripWidth: number;
  targetSpeed: number;
  targetForce: number;
};

export function toRobotiq2FMoveCommand(
  command: Robotiq2FCommand,
): Robotiq2FMoveCommand {
  const { controlKind, targetGripWidth, targetSpeed, targetForce } = command;

  if (
    controlKind !== 'move' ||
    targetGripWidth === undefined ||
    targetSpeed === undefined ||
    targetForce === undefined
  ) {
    throw new Error(
      'Robotiq2F move command requires targetGripWidth, targetSpeed and targetForce',
    );
  }

  return {
    ...command,
    controlKind,
    targetGripWidth,
    targetSpeed,
    targetForce,
  };
}
